/**
 * F37 (Wave 2D) — review-tag Add / Edit dialog (CMS).
 *
 * Add mode: `code` is editable (lower_snake_case). Edit mode: `code` is
 * read-only (the tag is keyed by code; PATCH is by code).
 *
 * Fields: code, name (en), description (en, optional), sport-scope multi-select
 * (null = all sports), active Switch, sort_order NumberInput. Validates against
 * `UpsertReviewTagInput`.
 *
 * Composes F02 primitives only + the local SportScopePicker.
 */
import { useEffect, useMemo, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import {
  Banner,
  Button,
  FormField,
  Modal,
  NumberInput,
  Switch,
  Textarea,
  TextInput,
} from '@bravemove/ui-web';
import {
  UpsertReviewTagInputSchema,
  type ReviewTagEntry,
  type SportName,
  type UpsertReviewTagInput,
} from '@bravemove/types';
import { SportScopePicker } from './SportScopePicker';

export interface ReviewTagEditDialogProps {
  readonly open: boolean;
  /** When provided, the dialog is in edit mode (code read-only). */
  readonly tag?: ReviewTagEntry | null;
  readonly onSave: (input: UpsertReviewTagInput) => Promise<void>;
  readonly onClose: () => void;
  readonly errorMessage?: string | null;
}

export function ReviewTagEditDialog({
  open,
  tag,
  onSave,
  onClose,
  errorMessage,
}: ReviewTagEditDialogProps) {
  const { t } = useI18n();
  const isEdit = Boolean(tag);

  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [sportScope, setSportScope] = useState<readonly SportName[] | null>(null);
  const [active, setActive] = useState(true);
  const [sortOrder, setSortOrder] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!open) return;
    if (tag) {
      setCode(tag.code);
      setName(tag.name.en ?? '');
      setDescription(tag.description?.en ?? '');
      setSportScope(tag.sportScope ?? null);
      setActive(tag.active);
      setSortOrder(tag.sortOrder);
    } else {
      setCode('');
      setName('');
      setDescription('');
      setSportScope(null);
      setActive(true);
      setSortOrder(0);
    }
  }, [open, tag]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const payload: UpsertReviewTagInput = useMemo(
    () => ({
      code,
      name: { en: name },
      ...(description.trim() ? { description: { en: description } } : {}),
      ...(sportScope !== null ? { sportScope: [...sportScope] } : {}),
      active,
      sortOrder,
    }),
    [code, name, description, sportScope, active, sortOrder],
  );

  const sportScopeValid = sportScope === null || sportScope.length >= 1;
  const valid = useMemo(
    () => sportScopeValid && UpsertReviewTagInputSchema.safeParse(payload).success,
    [payload, sportScopeValid],
  );

  const submit = async (): Promise<void> => {
    if (!valid) return;
    setSubmitting(true);
    try {
      await onSave(payload);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="md"
      title={
        isEdit
          ? t('admin:recommendation.catalog.tags.edit_title', { defaultValue: 'Edit review tag' })
          : t('admin:recommendation.catalog.tags.add_title', { defaultValue: 'Add review tag' })
      }
      footer={
        <>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={submitting}
            data-testid="tag-edit-cancel"
          >
            {t('admin:recommendation.catalog.cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button
            variant="primary"
            onClick={() => void submit()}
            loading={submitting}
            disabled={!valid || submitting}
            data-testid="tag-edit-submit"
          >
            {t('admin:recommendation.catalog.save', { defaultValue: 'Save' })}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3" data-testid="tag-edit-dialog">
        {errorMessage ? <Banner tone="danger">{errorMessage}</Banner> : null}

        <FormField
          label={t('recommendation:catalog.code_label', { defaultValue: 'Code' })}
          required
          helper={t('admin:recommendation.catalog.code_helper', {
            defaultValue: 'Lowercase letters, numbers and underscores.',
          })}
        >
          <TextInput
            value={code}
            onChange={(e) => setCode(e.target.value)}
            disabled={isEdit}
            placeholder={t('admin:recommendation.catalog.code_placeholder_tag', {
              defaultValue: 'patient',
            })}
            data-testid="tag-edit-code"
          />
        </FormField>

        <FormField
          label={t('recommendation:catalog.name_label', { defaultValue: 'Name' })}
          required
        >
          <TextInput
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('admin:recommendation.catalog.name_placeholder_tag', {
              defaultValue: 'Patient',
            })}
            data-testid="tag-edit-name"
          />
        </FormField>

        <FormField
          label={t('recommendation:catalog.description_label', { defaultValue: 'Description' })}
        >
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            maxLength={280}
            data-testid="tag-edit-description"
          />
        </FormField>

        <FormField
          label={t('recommendation:catalog.sport_scope_label', { defaultValue: 'Sport scope' })}
        >
          <SportScopePicker value={sportScope} onChange={setSportScope} disabled={submitting} />
        </FormField>

        <div className="grid grid-cols-2 gap-3">
          <FormField
            label={t('recommendation:catalog.sort_order_label', { defaultValue: 'Sort order' })}
          >
            <NumberInput
              value={sortOrder}
              onChange={setSortOrder}
              min={0}
              data-testid="tag-edit-sort"
            />
          </FormField>
          <FormField label={t('recommendation:catalog.active_label', { defaultValue: 'Active' })}>
            <Switch
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
              data-testid="tag-edit-active"
            />
          </FormField>
        </div>
      </div>
    </Modal>
  );
}
